/**
 * 이상한 방 - 별명(nickname) 데이터
 *
 * 근거 문서: 로드맵 확정안 3.2절(별명 - 로비·참가자 줄·순위표) · 5절(어휘: 별명/nickname, '타이틀'은 쓰지 않는다)
 *
 * ## 파일 이름이 `titles`인데 안의 말은 전부 '별명'인 이유
 *
 * 로드맵이 파일을 `titles.ts`로 못박았고, 화면 어휘는 GLOSSARY 5절이 '별명'으로 못박았다.
 * 파일 이름은 코드의 말이고 화면에 나오지 않으므로 그대로 두되, 안에서는 한 번도 '타이틀'이라 부르지 않는다 -
 * i18n 키도 `nickname.<id>`다. `title.*`는 이미 **타이틀 화면**(`title.start`)이 쓰는 접두사라,
 * 별명을 거기 얹으면 "런타임에 조립하는 키" 검사가 타이틀 화면의 키를 별명으로 오해한다.
 *
 * ## 별명은 규칙에 아무 영향이 없다
 *
 * 로비·참가자 줄·순위표에 이름 옆으로 붙는 한 줄이다. 그래서 여기 있는 것은 id와 i18n 키 이름뿐이고,
 * 문구는 `i18n/ko.ts`·`en.ts`에 있다. 어느 등급에서 나오는가는 봉투 표(`envelopeTable.ts`)가 정한다 -
 * 별명 자체에 등급을 적어 두면 표와 데이터가 두 번 말하게 된다.
 *
 * ## 표에 없는 별명은 죽은 데이터다
 *
 * 지금은 R 60 · SR 35 · SSR 15 = 110이다(3.2절의 열하나에서 열 배). 어느 등급에 들어가는지는
 * 여기가 아니라 `envelopeTable.ts`의 목록이 정한다. 표에 없는 별명을 여기 더 두면 그것은 얻을 방법이
 * 없는 데이터고, `validateEnvelopeTable`이 "풀에 없는 별명"으로 잡는다. 늘리려면 표부터 고쳐라.
 *
 * ## 딱 하나가 봉투 밖에 있다
 *
 * 완주 보상 별명(`source: 'complete'`)은 **풀에 없어야 한다.** 다 모은 사람에게만 가는 것이라
 * 뽑혀 나오면 조건이 무너지고, 수집률의 분모에 넣으면 자기 자신이 자기 조건이 되어 100%가 오지 않는다.
 * 그래서 `validateEnvelopeTable`은 이 별명을 "풀에 없어야 하는 것"으로, 나머지 열하나를
 * "풀에 있어야 하는 것"으로 서로 반대로 검사한다(`looks.ts` 머리말의 같은 규칙).
 */

/* --- 데이터 ------------------------------------------------------------------ */

/**
 * 별명 전부. 문구의 톤은 가벼운 탐정물이다("빈 자리 감별사", "문 앞 단골") - 수사·범죄 어휘는 없다.
 * `as const`로 두어 `NicknameId`가 목록에서 파생되게 한다.
 */
export const NICKNAMES = [
  /* R 여섯. 한 번쯤은 누구나 겪는 순간의 이름들이다. */
  { id: 'emptySeatSpotter', nameKey: 'nickname.emptySeatSpotter', source: 'envelope' },
  { id: 'doorRegular', nameKey: 'nickname.doorRegular', source: 'envelope' },
  { id: 'chairCounter', nameKey: 'nickname.chairCounter', source: 'envelope' },
  { id: 'cornerWatcher', nameKey: 'nickname.cornerWatcher', source: 'envelope' },
  { id: 'firstShotFirst', nameKey: 'nickname.firstShotFirst', source: 'envelope' },
  { id: 'lastSecondFinder', nameKey: 'nickname.lastSecondFinder', source: 'envelope' },

  /* SR 넷. 잘하는 사람의 이름들이다. */
  { id: 'turnedThingsNemesis', nameKey: 'nickname.turnedThingsNemesis', source: 'envelope' },
  { id: 'colorHawk', nameKey: 'nickname.colorHawk', source: 'envelope' },
  { id: 'noMissNoFuss', nameKey: 'nickname.noMissNoFuss', source: 'envelope' },
  { id: 'quietObserver', nameKey: 'nickname.quietObserver', source: 'envelope' },

  /* SSR 하나. 이 게임의 은유 그 자체("누가 다녀갔다")를 이름으로 단다. */
  { id: 'visitor', nameKey: 'nickname.visitor', source: 'envelope' },
  { id: 'sameRouteEveryTime', nameKey: 'nickname.sameRouteEveryTime', source: 'envelope' },
  { id: 'alwaysClockwise', nameKey: 'nickname.alwaysClockwise', source: 'envelope' },
  { id: 'oneLapOnArrival', nameKey: 'nickname.oneLapOnArrival', source: 'envelope' },
  { id: 'windowsFirst', nameKey: 'nickname.windowsFirst', source: 'envelope' },
  { id: 'looksUpToo', nameKey: 'nickname.looksUpToo', source: 'envelope' },
  { id: 'eyesOnTheFloor', nameKey: 'nickname.eyesOnTheFloor', source: 'envelope' },
  { id: 'behindTheFurniture', nameKey: 'nickname.behindTheFurniture', source: 'envelope' },
  { id: 'tiltsTheHeadFirst', nameKey: 'nickname.tiltsTheHeadFirst', source: 'envelope' },
  { id: 'rightUpClose', nameKey: 'nickname.rightUpClose', source: 'envelope' },
  { id: 'takesAStepBack', nameKey: 'nickname.takesAStepBack', source: 'envelope' },
  { id: 'holdsABreath', nameKey: 'nickname.holdsABreath', source: 'envelope' },
  { id: 'doubtsItFirst', nameKey: 'nickname.doubtsItFirst', source: 'envelope' },
  { id: 'staresAtWhatStayed', nameKey: 'nickname.staresAtWhatStayed', source: 'envelope' },
  { id: 'sameSpotTwice', nameKey: 'nickname.sameSpotTwice', source: 'envelope' },
  { id: 'countsTheShotsFirst', nameKey: 'nickname.countsTheShotsFirst', source: 'envelope' },
  { id: 'alwaysOneLeft', nameKey: 'nickname.alwaysOneLeft', source: 'envelope' },
  { id: 'onlyWhenSure', nameKey: 'nickname.onlyWhenSure', source: 'envelope' },
  { id: 'allAtOnce', nameKey: 'nickname.allAtOnce', source: 'envelope' },
  { id: 'shootAndMoveOn', nameKey: 'nickname.shootAndMoveOn', source: 'envelope' },
  { id: 'threeThenABreather', nameKey: 'nickname.threeThenABreather', source: 'envelope' },
  { id: 'keepsLookingAnyway', nameKey: 'nickname.keepsLookingAnyway', source: 'envelope' },
  { id: 'originalRoomAgain', nameKey: 'nickname.originalRoomAgain', source: 'envelope' },
  { id: 'usesEverySecond', nameKey: 'nickname.usesEverySecond', source: 'envelope' },
  { id: 'staysForTheResults', nameKey: 'nickname.staysForTheResults', source: 'envelope' },
  { id: 'straightBackIn', nameKey: 'nickname.straightBackIn', source: 'envelope' },
  { id: 'framesLevel', nameKey: 'nickname.framesLevel', source: 'envelope' },
  { id: 'plantFriend', nameKey: 'nickname.plantFriend', source: 'envelope' },
  { id: 'lampOnly', nameKey: 'nickname.lampOnly', source: 'envelope' },
  { id: 'drawerGap', nameKey: 'nickname.drawerGap', source: 'envelope' },
  { id: 'rugCorner', nameKey: 'nickname.rugCorner', source: 'envelope' },
  { id: 'clockHands', nameKey: 'nickname.clockHands', source: 'envelope' },
  { id: 'cupHandle', nameKey: 'nickname.cupHandle', source: 'envelope' },
  { id: 'cushionSquare', nameKey: 'nickname.cushionSquare', source: 'envelope' },
  { id: 'bookSpines', nameKey: 'nickname.bookSpines', source: 'envelope' },
  { id: 'windowLatch', nameKey: 'nickname.windowLatch', source: 'envelope' },
  { id: 'doorknobEye', nameKey: 'nickname.doorknobEye', source: 'envelope' },
  { id: 'outletCount', nameKey: 'nickname.outletCount', source: 'envelope' },
  { id: 'bulbDuty', nameKey: 'nickname.bulbDuty', source: 'envelope' },
  { id: 'mirrorToo', nameKey: 'nickname.mirrorToo', source: 'envelope' },
  { id: 'chairAngle', nameKey: 'nickname.chairAngle', source: 'envelope' },
  { id: 'shelfLine', nameKey: 'nickname.shelfLine', source: 'envelope' },
  { id: 'curtainFolds', nameKey: 'nickname.curtainFolds', source: 'envelope' },
  { id: 'switchWay', nameKey: 'nickname.switchWay', source: 'envelope' },
  { id: 'calendarPage', nameKey: 'nickname.calendarPage', source: 'envelope' },
  { id: 'slipperPair', nameKey: 'nickname.slipperPair', source: 'envelope' },
  { id: 'trashBin', nameKey: 'nickname.trashBin', source: 'envelope' },
  { id: 'remoteSpot', nameKey: 'nickname.remoteSpot', source: 'envelope' },
  { id: 'keysThere', nameKey: 'nickname.keysThere', source: 'envelope' },
  { id: 'wallpaperPattern', nameKey: 'nickname.wallpaperPattern', source: 'envelope' },
  { id: 'pencilOne', nameKey: 'nickname.pencilOne', source: 'envelope' },
  { id: 'lastOneIn', nameKey: 'nickname.lastOneIn', source: 'envelope' },
  { id: 'inNoHurry', nameKey: 'nickname.inNoHurry', source: 'envelope' },
  { id: 'skipsTheBriefing', nameKey: 'nickname.skipsTheBriefing', source: 'envelope' },
  { id: 'cameOutEmptyHanded', nameKey: 'nickname.cameOutEmptyHanded', source: 'envelope' },
  { id: 'justWarmingUp', nameKey: 'nickname.justWarmingUp', source: 'envelope' },
  { id: 'nextRoundForReal', nameKey: 'nickname.nextRoundForReal', source: 'envelope' },
  { id: 'theGuessLanded', nameKey: 'nickname.theGuessLanded', source: 'envelope' },
  { id: 'justAFeeling', nameKey: 'nickname.justAFeeling', source: 'envelope' },
  { id: 'luckyKindOfDay', nameKey: 'nickname.luckyKindOfDay', source: 'envelope' },
  { id: 'rightBehindYou', nameKey: 'nickname.rightBehindYou', source: 'envelope' },
  { id: 'trustsTheNeighbor', nameKey: 'nickname.trustsTheNeighbor', source: 'envelope' },
  { id: 'gaspedAlong', nameKey: 'nickname.gaspedAlong', source: 'envelope' },
  { id: 'hereForTheRoom', nameKey: 'nickname.hereForTheRoom', source: 'envelope' },
  { id: 'likesThePlace', nameKey: 'nickname.likesThePlace', source: 'envelope' },
  { id: 'intoTheWallpaper', nameKey: 'nickname.intoTheWallpaper', source: 'envelope' },
  { id: 'complimentsTheLighting', nameKey: 'nickname.complimentsTheLighting', source: 'envelope' },
  { id: 'triesTheChair', nameKey: 'nickname.triesTheChair', source: 'envelope' },
  { id: 'wantsToTidyUp', nameKey: 'nickname.wantsToTidyUp', source: 'envelope' },
  { id: 'wouldLiveHere', nameKey: 'nickname.wouldLiveHere', source: 'envelope' },
  { id: 'bigReactions', nameKey: 'nickname.bigReactions', source: 'envelope' },
  { id: 'clapsTheLoudest', nameKey: 'nickname.clapsTheLoudest', source: 'envelope' },
  { id: 'thinkingOutLoud', nameKey: 'nickname.thinkingOutLoud', source: 'envelope' },
  { id: 'laughedAndMissed', nameKey: 'nickname.laughedAndMissed', source: 'envelope' },
  { id: 'lostTrackOfTime', nameKey: 'nickname.lostTrackOfTime', source: 'envelope' },
  { id: 'eyesFirst', nameKey: 'nickname.eyesFirst', source: 'envelope' },
  { id: 'quickHands', nameKey: 'nickname.quickHands', source: 'envelope' },
  { id: 'fasterThanClock', nameKey: 'nickname.fasterThanClock', source: 'envelope' },
  { id: 'allInOneGo', nameKey: 'nickname.allInOneGo', source: 'envelope' },
  { id: 'rightEveryTime', nameKey: 'nickname.rightEveryTime', source: 'envelope' },
  { id: 'noWastedMove', nameKey: 'nickname.noWastedMove', source: 'envelope' },
  { id: 'fastAndSure', nameKey: 'nickname.fastAndSure', source: 'envelope' },
  { id: 'firstGuessRight', nameKey: 'nickname.firstGuessRight', source: 'envelope' },
  { id: 'steadyHand', nameKey: 'nickname.steadyHand', source: 'envelope' },
  { id: 'neverRattled', nameKey: 'nickname.neverRattled', source: 'envelope' },
  { id: 'inNoRush', nameKey: 'nickname.inNoRush', source: 'envelope' },
  { id: 'neverBlinks', nameKey: 'nickname.neverBlinks', source: 'envelope' },
  { id: 'oneAfterAnother', nameKey: 'nickname.oneAfterAnother', source: 'envelope' },
  { id: 'unbrokenRun', nameKey: 'nickname.unbrokenRun', source: 'envelope' },
  { id: 'alwaysInForm', nameKey: 'nickname.alwaysInForm', source: 'envelope' },
  { id: 'notAScratch', nameKey: 'nickname.notAScratch', source: 'envelope' },
  { id: 'cleanRecord', nameKey: 'nickname.cleanRecord', source: 'envelope' },
  { id: 'cleanFinish', nameKey: 'nickname.cleanFinish', source: 'envelope' },
  { id: 'comesFromBehind', nameKey: 'nickname.comesFromBehind', source: 'envelope' },
  { id: 'turnsItAround', nameKey: 'nickname.turnsItAround', source: 'envelope' },
  { id: 'oneForTheRecord', nameKey: 'nickname.oneForTheRecord', source: 'envelope' },
  { id: 'goodInstincts', nameKey: 'nickname.goodInstincts', source: 'envelope' },
  { id: 'knowsWhereToLook', nameKey: 'nickname.knowsWhereToLook', source: 'envelope' },
  { id: 'seesItThrough', nameKey: 'nickname.seesItThrough', source: 'envelope' },
  { id: 'patientEye', nameKey: 'nickname.patientEye', source: 'envelope' },

  /* 완주 보상 하나. 봉투에서 나오는 것을 전부 모은 사람에게만 간다(`inventory.settleCompletion`). */
  { id: 'sawItAll', nameKey: 'nickname.sawItAll', source: 'complete' },
] as const satisfies readonly NicknameShape[]

/** `NICKNAMES`의 한 줄이 갖춰야 하는 모양. 출처의 뜻은 `looks.ts`의 `LookSource`와 같다(별명은 기본이 없어 둘뿐이다). */
type NicknameShape = Readonly<{ id: string; nameKey: `nickname.${string}`; source: 'envelope' | 'complete' }>

export type NicknameDef = (typeof NICKNAMES)[number]
export type NicknameId = NicknameDef['id']

const NICKNAME_BY_ID: ReadonlyMap<string, NicknameDef> = new Map(NICKNAMES.map((nick) => [nick.id, nick]))

/* --- 조회 -------------------------------------------------------------------- */

export function isNicknameId(value: unknown): value is NicknameId {
  return typeof value === 'string' && NICKNAME_BY_ID.has(value)
}

export function getNickname(id: NicknameId): NicknameDef {
  const nick = NICKNAME_BY_ID.get(id)
  // `NicknameId`는 목록에서 파생된 유니온이라 여기 도달할 수 없다. 형식 서술이다.
  if (nick === undefined) throw new RangeError(`별명 '${id}'가 없다`)
  return nick
}

/** 봉투에서 나올 수 있는 별명. 수집률의 분모이자 `validateEnvelopeTable`이 "풀에 있어야 한다"로 검사하는 목록이다. */
export function obtainableNicknames(): readonly NicknameDef[] {
  return NICKNAMES.filter((nick) => nick.source === 'envelope')
}

/** 다 모은 사람에게만 가는 별명. 풀에도 분모에도 없다. */
export function completionNicknames(): readonly NicknameDef[] {
  return NICKNAMES.filter((nick) => nick.source === 'complete')
}

/* --- 검증 -------------------------------------------------------------------- */

/** 별명 표를 검사한다. 문제가 없으면 빈 배열. */
export function validateNicknames(nicknames: readonly NicknameShape[] = NICKNAMES): readonly string[] {
  const issues: string[] = []
  const seen = new Set<string>()

  for (const nick of nicknames) {
    if (seen.has(nick.id)) issues.push(`별명 id '${nick.id}'가 겹친다`)
    seen.add(nick.id)
    if (nick.nameKey !== `nickname.${nick.id}`) issues.push(`'${nick.id}'의 키가 id와 어긋난다: ${nick.nameKey}`)
  }

  /*
   * 완주 보상 별명이 없으면 다 모아도 별명 자리에는 아무 일도 일어나지 않고, 둘이면 하나는 얻자마자
   * 다른 하나에 가려진다(사물함은 자리가 아니라 목록이라 둘 다 보이기는 한다 - 그래도 "보상 하나"라는
   * 약속이 깨진 것이다). 꾸밈 쪽과 같은 이유로 정확히 하나다.
   */
  const complete = nicknames.filter((nick) => nick.source === 'complete').length
  if (complete !== 1) issues.push(`완주 보상 별명이 ${String(complete)}개다 - 정확히 하나여야 한다`)

  return issues
}
